import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  HandCoins,
  ArrowLeft, ArrowRight, Camera, CheckCircle2, Fuel, UtensilsCrossed, BedDouble,
  Milestone, TrainFront, ShoppingBag, Route, MoreHorizontal, Trash2, Paperclip, Send,
} from "lucide-react";

const CATEGORY_ICONS = {
  CARBURANT: Fuel, KM: Route, REPAS: UtensilsCrossed, HOTEL: BedDouble,
  PEAGE: Milestone, TRANSPORT: TrainFront, FOURNITURES: ShoppingBag, AUTRE: MoreHorizontal,
};

const STEPS = ["Période", "Dépense", "Justificatif", "Résumé"];

export default function NewReportWizard() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const kmRate = user?.km_rate ?? 0.17;
  const [step, setStep] = useState(0);
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [category, setCategory] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [distanceKm, setDistanceKm] = useState("");
  const [isDonation, setIsDonation] = useState(false);
  const [expense, setExpense] = useState(null);
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    if (reportId) {
      api.get(`/expense-reports/${reportId}`)
        .then(({ data }) => {
          if (data.report.status !== "DRAFT") {
            navigate(`/notes/${reportId}`, { replace: true });
            return;
          }
          setReport(data.report);
          // Brouillon avec dépenses existantes (ex: note dupliquée) → aller directement au résumé
          setStep((data.report.expenses || []).length > 0 ? 3 : 1);
        })
        .catch(() => navigate("/accueil", { replace: true }));
    }
  }, [reportId, navigate]);

  const kmCategory = category?.is_km;
  const amountCents = useMemo(() => Math.round(parseFloat((amount || "0").replace(",", ".")) * 100) || 0, [amount]);

  const startReport = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/expense-reports", { period });
      setReport(data.report);
      setStep(1);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const saveExpense = async () => {
    setError("");
    if (!category) return setError("Choisissez une catégorie de dépense.");
    if (!date) return setError("Indiquez la date de la dépense.");
    if (!amountCents || amountCents <= 0) return setError("Indiquez un montant supérieur à 0 €.");
    if (purpose.trim().length < 3) return setError("Précisez le motif de la dépense (au moins 3 caractères).");
    if (kmCategory && (!distanceKm || parseFloat(distanceKm) <= 0)) return setError("Indiquez le nombre de kilomètres parcourus.");
    setLoading(true);
    try {
      const payload = {
        category_id: category.id, date, amount: amountCents / 100,
        purpose: purpose.trim(), distance_km: kmCategory ? parseFloat(distanceKm) : null,
        is_donation: isDonation,
      };
      const { data } = await api.post(`/expense-reports/${report.id}/expenses`, payload);
      setExpense(data.expense);
      setUploaded([]);
      setFile(null);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async () => {
    setError("");
    if (!file) return setError("Choisissez une photo ou un fichier d'abord.");
    if (file.size > 10 * 1024 * 1024) return setError("Fichier trop volumineux (maximum 10 Mo).");
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/expenses/${expense.id}/attachments`, form);
      setUploaded((prev) => [...prev, data.attachment]);
      setFile(null);
      toast.success("Justificatif bien enregistré");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const finishExpense = async () => {
    setError("");
    if (category?.requires_receipt && uploaded.length === 0) {
      return setError("Cette catégorie exige un justificatif. Ajoutez une photo ou un fichier.");
    }
    await reloadReport();
    resetExpenseForm();
    setStep(3);
  };

  const reloadReport = async () => {
    const { data } = await api.get(`/expense-reports/${report.id}`);
    setReport(data.report);
  };

  const resetExpenseForm = () => {
    setCategory(null);
    setAmount("");
    setPurpose("");
    setDistanceKm("");
    setIsDonation(false);
    setExpense(null);
    setUploaded([]);
    setFile(null);
  };

  const addAnother = () => {
    resetExpenseForm();
    setStep(1);
  };

  const removeExpense = async (expenseId) => {
    try {
      await api.delete(`/expenses/${expenseId}`);
      await reloadReport();
      toast.success("Dépense supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadToExpense = async (expenseId, f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Fichier trop volumineux (maximum 10 Mo).");
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/expenses/${expenseId}/attachments`, form);
      await reloadReport();
      toast.success("Justificatif enregistré");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitReport = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/expense-reports/${report.id}/submit`);
      toast.success(data.auto_validated
        ? "Votre note a été enregistrée et validée automatiquement (aucun responsable hiérarchique)."
        : "Votre note a été envoyée au valideur !");
      navigate("/accueil", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-enter" data-testid="new-report-wizard">
      <div>
        <Link to="/accueil" className="text-[#0369a1] font-semibold inline-flex items-center gap-2 mb-3" data-testid="wizard-back-home">
          <ArrowLeft className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Retour à l'accueil
        </Link>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Nouvelle note de frais</h1>
        {report && <p className="text-slate-600 mt-1 text-lg">{report.number} — brouillon sauvegardé automatiquement</p>}
      </div>

      <div className="flex gap-2" aria-label="Progression">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1">
            <div className={`h-2 rounded-full ${i <= step ? "bg-[#0369a1]" : "bg-slate-200"}`} />
            <p className={`text-xs sm:text-sm mt-1 font-semibold ${i <= step ? "text-[#0369a1]" : "text-slate-400"}`}>{label}</p>
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="card-base space-y-6" data-testid="step-period">
          <h2 className="text-2xl font-semibold text-slate-900">Pour quelle période ?</h2>
          <div>
            <Label htmlFor="period" className="label-lg">Mois concerné par vos dépenses</Label>
            <Input id="period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="input-lg" data-testid="period-input" />
          </div>
          {error && <p className="text-[#b91c1c] font-semibold" role="alert" data-testid="wizard-error">{error}</p>}
          <button onClick={startReport} disabled={loading || !period} className="btn-primary-lg w-full" data-testid="period-continue-button">
            {loading ? "Création…" : "Continuer"} <ArrowRight className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="card-base space-y-6" data-testid="step-expense">
          <h2 className="text-2xl font-semibold text-slate-900">Ajouter une dépense</h2>
          <div>
            <p className="label-lg">1. De quel type de dépense s'agit-il ?</p>
            <div className="grid grid-cols-2 gap-3" role="radiogroup">
              {categories.map((c) => {
                const Icon = CATEGORY_ICONS[c.code] || MoreHorizontal;
                const selected = category?.code === c.code;
                return (
                  <button key={c.code} type="button" onClick={() => setCategory(c)}
                    data-testid={`category-${c.code}`}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all active:scale-[0.98] ${
                      selected ? "border-[#0369a1] bg-sky-50 ring-4 ring-[#0369a1]/20" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}>
                    <Icon className={`h-8 w-8 ${selected ? "text-[#0369a1]" : "text-slate-500"}`} aria-hidden="true" strokeWidth={2.5} />
                    <span className={`text-sm sm:text-base font-semibold text-center ${selected ? "text-[#0369a1]" : "text-slate-700"}`}>{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label htmlFor="expense-date" className="label-lg">2. Date de la dépense</Label>
            <Input id="expense-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="input-lg" data-testid="expense-date-input" />
          </div>
          {kmCategory && (
            <div>
              <Label htmlFor="expense-km" className="label-lg">Nombre de kilomètres</Label>
              <Input id="expense-km" type="number" min="0" step="0.1" inputMode="decimal" value={distanceKm}
                onChange={(e) => {
                  setDistanceKm(e.target.value);
                  const km = parseFloat(e.target.value) || 0;
                  setAmount(km > 0 ? (km * kmRate).toFixed(2) : "");
                }} className="input-lg" placeholder="Ex : 45" data-testid="expense-km-input" />
              <p className="text-slate-600 mt-2">Barème appliqué : <strong>{String(kmRate).replace(".", ",")} € / km</strong> (défini par l'administrateur sur votre profil)</p>
            </div>
          )}
          <div>
            <Label htmlFor="expense-amount" className="label-lg">{kmCategory ? "Montant calculé (km × barème)" : "3. Montant en euros"}</Label>
            <div className="relative">
              <Input id="expense-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
                readOnly={kmCategory}
                className={`input-lg pr-12 ${kmCategory ? "bg-slate-100 font-bold" : ""}`} placeholder="Ex : 42,50" data-testid="expense-amount-input" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-500">€</span>
            </div>
          </div>
          <div>
            <Label htmlFor="expense-purpose" className="label-lg">4. Motif de la dépense</Label>
            <Textarea id="expense-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}
              className="text-lg border-2 border-slate-300 rounded-xl px-4 py-3 min-h-24 w-full focus:border-[#0369a1]"
              placeholder="Ex : déplacement paroisse de Foix" data-testid="expense-purpose-input" />
          </div>
          <label htmlFor="expense-donation" className="flex items-start gap-3 border-2 border-rose-200 rounded-xl p-4 cursor-pointer hover:bg-rose-50/50 transition-colors" data-testid="expense-donation-label">
            <input id="expense-donation" type="checkbox" checked={isDonation} onChange={(e) => setIsDonation(e.target.checked)}
              className="h-6 w-6 mt-1 accent-rose-700" data-testid="expense-donation-checkbox" />
            <span>
              <span className="font-bold text-slate-900 text-lg flex items-center gap-2">
                <HandCoins className="h-6 w-6 text-rose-600" aria-hidden="true" strokeWidth={2.5} /> J'abandonne cette dépense (transfert au denier)
              </span>
              {isDonation && (
                <span className="block text-slate-600 mt-1" data-testid="expense-donation-hint">
                  Je renonce à être remboursé de cette dépense : son montant sera <strong>transféré au denier</strong>,
                  pris en compte une fois la note validée.
                </span>
              )}
            </span>
          </label>
          {error && <p className="text-[#b91c1c] font-semibold" role="alert" data-testid="wizard-error">{error}</p>}
          <button onClick={saveExpense} disabled={loading} className="btn-primary-lg w-full" data-testid="expense-continue-button">
            {loading ? "Enregistrement…" : "Continuer"} <ArrowRight className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {step === 2 && expense && (
        <div className="card-base space-y-6" data-testid="step-receipt">
          <h2 className="text-2xl font-semibold text-slate-900">Justificatif</h2>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="font-semibold text-slate-800">{expense.category_label || ""} — {fmtEuros(expense.amount_cents)}</p>
            <p className="text-slate-600">{expense.date} — {expense.purpose}</p>
          </div>
          {category?.requires_receipt ? (
            <p className="text-[#b45309] font-semibold">Un justificatif est obligatoire pour cette catégorie.</p>
          ) : (
            <p className="text-slate-600">Le justificatif est facultatif pour cette catégorie, mais recommandé.</p>
          )}
          <div className="space-y-4">
            <label htmlFor="receipt-file" className="btn-secondary-lg w-full cursor-pointer" data-testid="receipt-file-label">
              <Camera className="h-6 w-6" aria-hidden="true" strokeWidth={2.5} />
              {file ? file.name : "Prendre une photo ou choisir un fichier"}
            </label>
            <input id="receipt-file" type="file" accept="image/*,.pdf,.heic" capture="environment" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="receipt-file-input" />
            {file && (
              <button onClick={uploadFile} disabled={loading} className="btn-primary-lg w-full" data-testid="receipt-upload-button">
                {loading ? "Envoi en cours…" : "Enregistrer ce justificatif"}
              </button>
            )}
            {uploaded.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-xl p-4" data-testid={`uploaded-attachment-${a.id}`}>
                <CheckCircle2 className="h-6 w-6 text-[#15803d]" aria-hidden="true" strokeWidth={2.5} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-green-900 truncate">{a.filename}</p>
                  <p className="text-sm text-green-700">Justificatif enregistré ({Math.round(a.size / 1024)} Ko)</p>
                </div>
              </div>
            ))}
            {uploaded.length > 0 && (
              <p className="text-slate-600 text-center">Vous pouvez ajouter un autre justificatif ou continuer.</p>
            )}
          </div>
          {error && <p className="text-[#b91c1c] font-semibold" role="alert" data-testid="wizard-error">{error}</p>}
          <div className="flex gap-3 flex-col sm:flex-row">
            <button onClick={() => setStep(1)} className="btn-secondary-lg flex-1" data-testid="receipt-back-button">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Retour
            </button>
            <button onClick={finishExpense} disabled={loading} className="btn-primary-lg flex-1" data-testid="receipt-finish-button">
              Continuer <ArrowRight className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      {step === 3 && report && (
        <div className="card-base space-y-6" data-testid="step-summary">
          <h2 className="text-2xl font-semibold text-slate-900">Vérifiez avant l'envoi</h2>
          <div className="space-y-3">
            {(report.expenses || []).map((e) => (
              <div key={e.id} className="border-2 border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid={`summary-expense-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 text-lg">{e.category_label}</p>
                  <p className="text-slate-600">{e.date} — {e.purpose}</p>
                  {e.is_donation && (
                    <p className="inline-flex items-center gap-1 mt-1 text-rose-700 font-semibold" data-testid={`summary-donation-${e.id}`}>
                      <HandCoins className="h-4 w-4" aria-hidden="true" /> Abandonnée (transfert au denier)
                    </p>
                  )}
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                    <Paperclip className="h-4 w-4" aria-hidden="true" /> {(e.attachments || []).length} justificatif(s)
                  </p>
                  <label className="mt-2 inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 border-[#0369a1] text-[#0369a1] font-semibold text-sm cursor-pointer hover:bg-sky-50"
                    data-testid={`summary-upload-label-${e.id}`}>
                    <Camera className="h-4 w-4" aria-hidden="true" strokeWidth={2.5} /> Ajouter un justificatif
                    <input type="file" accept="image/*,.pdf,.heic" className="hidden"
                      onChange={(ev) => { uploadToExpense(e.id, ev.target.files?.[0]); ev.target.value = ""; }}
                      data-testid={`summary-upload-${e.id}`} />
                  </label>
                </div>
                <div className="text-right">
                  <p className="font-bold text-xl text-slate-900">{fmtEuros(e.amount_cents)}</p>
                  <button onClick={() => removeExpense(e.id)} className="text-[#b91c1c] font-semibold text-sm inline-flex items-center gap-1 mt-1"
                    data-testid={`remove-expense-${e.id}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-sky-50 border-2 border-[#0369a1] rounded-xl p-5 flex items-center justify-between">
            <p className="text-lg font-semibold text-slate-800">Total demandé</p>
            <p className="text-3xl font-bold text-[#0369a1]" data-testid="summary-total">{fmtEuros(report.total_requested_cents)}</p>
          </div>
          {(report.total_donation_cents || 0) > 0 && (
            <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-5 flex items-center justify-between" data-testid="summary-donation-total">
              <p className="text-lg font-semibold text-rose-800 flex items-center gap-2">
                <HandCoins className="h-5 w-5" aria-hidden="true" /> Dont transfert au denier (dépenses abandonnées)
              </p>
              <p className="text-2xl font-bold text-rose-700">{fmtEuros(report.total_donation_cents)}</p>
            </div>
          )}
          {error && <p className="text-[#b91c1c] font-semibold" role="alert" data-testid="wizard-error">{error}</p>}
          <div className="flex gap-3 flex-col">
            <button onClick={addAnother} className="btn-secondary-lg w-full" data-testid="add-another-expense-button">
              + Ajouter une autre dépense
            </button>
            <button onClick={submitReport} disabled={submitting || (report.expenses || []).length === 0}
              className="btn-success-lg w-full" data-testid="submit-report-button">
              <Send className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} />
              {submitting ? "Envoi en cours…" : "Envoyer ma note de frais"}
            </button>
          </div>
          <p className="text-slate-500 text-center">
            Une fois envoyée, votre note sera examinée par votre responsable. Vous recevrez un email à chaque étape.
          </p>
        </div>
      )}
    </div>
  );
}
